import React, { useState, useEffect } from 'react';
import { Sparkles, AlertCircle, TrendingUp, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import PayPalCreditButton from './PayPalCreditButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AICreditsIndicator = ({ researcherId, compact = false, refreshTrigger = 0 }) => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState(50);

  useEffect(() => {
    if (researcherId) {
      fetchCredits();
    } else {
      setLoading(false);
    }
  }, [researcherId, refreshTrigger]);

  const fetchCredits = async () => {
    try {
      console.log('Fetching AI credits for researcher:', researcherId);
      console.log('Backend URL:', BACKEND_URL);
      const url = `${BACKEND_URL}/api/ai-credits/balance/${researcherId}`;
      console.log('Full URL:', url);
      
      const response = await fetch(url);
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Credits data received:', data);
        setCredits(data);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch credits:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error fetching AI credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseSuccess = (data) => {
    // Close dialog and refresh credits
    setPurchaseDialogOpen(false);
    fetchCredits();
  };

  const handlePurchaseCancel = () => {
    // Just close the dialog
    setPurchaseDialogOpen(false);
  };

  if (!researcherId) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Sparkles className="h-4 w-4 text-gray-400" />
        <span>{t('aiCreditsLoading')}</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${compact ? 'text-xs' : 'text-sm'}`}>
        <Sparkles className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} animate-pulse text-purple-500`} />
        <span className="text-gray-500">Loading credits...</span>
      </div>
    );
  }

  if (!credits) {
    return (
      <div className="flex flex-col gap-2 p-3 bg-orange-50 border border-orange-200 rounded">
        <div className="flex items-center gap-2 text-sm text-orange-600">
          <Sparkles className="h-4 w-4" />
          <span>Unable to load AI credits</span>
        </div>
        <div className="text-xs text-gray-600">
          Researcher ID: {researcherId || 'Not available'}
          <br />
          Check browser console (F12) for error details
        </div>
      </div>
    );
  }

  const percentage = (credits.credits_used / (credits.monthly_limit + credits.total_purchased_credits)) * 100;
  const isLow = credits.credits_remaining < 10;
  const isCritical = credits.credits_remaining === 0;

  const getPlanColor = (plan) => {
    switch (plan) {
      case 'advanced':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'premium':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  const getPlanName = (plan) => {
    switch (plan) {
      case 'advanced':
        return t('advanced');
      case 'premium':
        return t('premium');
      default:
        return t('basic');
    }
  };

  if (compact) {
    return (
      <>
        <div className="flex items-center gap-2">
          <Sparkles className={`h-4 w-4 ${isCritical ? 'text-red-500' : isLow ? 'text-yellow-500' : 'text-purple-500'}`} />
          <span className={`text-sm font-medium ${isCritical ? 'text-red-600' : isLow ? 'text-yellow-600' : 'text-gray-700'}`}>
            {credits.credits_remaining} {t('aiCreditsLabel')}
          </span>
          {isCritical && (
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-xs"
              onClick={() => setPurchaseDialogOpen(true)}
            >
              Add Credits
            </Button>
          )}
        </div>

        {/* Purchase Dialog */}
        <Dialog open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('purchaseAICredits')}</DialogTitle>
              <DialogDescription>
                {t('getMoreCredits')}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { credits: 50, price: 5.00 },
                  { credits: 100, price: 10.00 },
                  { credits: 200, price: 20.00 }
                ].map((pkg) => (
                  <Button
                    key={pkg.credits}
                    variant={purchaseAmount === pkg.credits ? "default" : "outline"}
                    onClick={() => setPurchaseAmount(pkg.credits)}
                    className="flex flex-col h-auto py-3"
                  >
                    <span className="text-lg font-bold">{pkg.credits}</span>
                    <span className="text-xs">{t('credits')}</span>
                    <span className="text-xs text-muted-foreground">${pkg.price.toFixed(2)}</span>
                  </Button>
                ))}
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <span className="font-medium">{t('total')}:</span>
                <span className="text-2xl font-bold">
                  ${purchaseAmount === 50 ? '5.00' : purchaseAmount === 100 ? '10.00' : '20.00'}
                </span>
              </div>

            </div>

            <div className="mt-4">
              <PayPalCreditButton
                researcherId={researcherId}
                creditsAmount={purchaseAmount}
                onSuccess={handlePurchaseSuccess}
                onCancel={handlePurchaseCancel}
              />
            </div>

            <DialogFooter className="mt-2">
              <Button variant="outline" onClick={() => setPurchaseDialogOpen(false)}>
                {t('cancel')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <div className="flex items-center gap-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
        {/* Icon and Title */}
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-600" />
          <span className="text-sm font-semibold text-gray-700">{t('aiCreditsLabel')}:</span>
        </div>

        {/* Credits Info */}
        <div className="flex-1 flex items-center gap-4">
          {/* Remaining Credits */}
          <div className="flex items-center gap-2">
            <span className={`text-2xl font-bold ${isCritical ? 'text-red-600' : isLow ? 'text-yellow-600' : 'text-purple-600'}`}>
              {credits.credits_remaining}
            </span>
            <span className="text-sm text-gray-600">
              / {credits.monthly_limit + credits.total_purchased_credits} credits
            </span>
          </div>

          {/* Progress Bar */}
          <div className="flex-1 max-w-md">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  isCritical ? 'bg-red-600' :
                  isLow ? 'bg-yellow-500' :
                  'bg-purple-600'
                }`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Percentage */}
          <span className={`text-sm font-medium ${
            isCritical ? 'text-red-600' :
            isLow ? 'text-yellow-600' :
            'text-purple-600'
          }`}>
            {percentage.toFixed(0)}% used
          </span>
        </div>

        {/* Plan Badge */}
        <Badge className={getPlanColor(credits.subscription_plan) + ' border'}>
          {getPlanName(credits.subscription_plan)} {t('plan')}
        </Badge>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {(isLow || isCritical) && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPurchaseDialogOpen(true)}
                className="flex items-center gap-2"
              >
                <ShoppingCart className="h-4 w-4" />
                {t('buyCredits')}
              </Button>
              <Button
                size="sm"
                onClick={() => setUpgradeDialogOpen(true)}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
              >
                <TrendingUp className="h-4 w-4" />
                {t('upgradePlan')}
              </Button>
            </>
          )}
        </div>

        {/* Warning Icon */}
        {isCritical && (
          <AlertCircle className="h-5 w-5 text-red-600" />
        )}
      </div>

      {/* Low Credits Warning */}
      {isLow && !isCritical && (
        <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <p className="font-medium">{t('runningLowCredits')}</p>
            <p className="mt-1">
              {t('creditsRemainingMessage', { count: credits.credits_remaining })}
            </p>
          </div>
        </div>
      )}

      {/* Out of Credits Warning */}
      {isCritical && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            <p className="font-medium">Out of AI credits!</p>
            <p className="mt-1">
              Purchase additional credits or upgrade your plan to continue using AI features.
            </p>
          </div>
        </div>
      )}

      {/* Purchase Dialog - Same as compact version */}
      <Dialog open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('purchaseAICredits')}</DialogTitle>
            <DialogDescription>
              {t('getMoreCredits')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-3">
              {[
                { credits: 50, price: 5.00 },
                { credits: 100, price: 10.00 },
                { credits: 200, price: 20.00 }
              ].map((pkg) => (
                <Button
                  key={pkg.credits}
                  variant={purchaseAmount === pkg.credits ? "default" : "outline"}
                  onClick={() => setPurchaseAmount(pkg.credits)}
                  className="flex flex-col h-auto py-3"
                >
                  <span className="text-lg font-bold">{pkg.credits}</span>
                  <span className="text-xs">{t('credits')}</span>
                  <span className="text-xs text-muted-foreground">${pkg.price.toFixed(2)}</span>
                </Button>
              ))}
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="font-medium">{t('total')}:</span>
              <span className="text-2xl font-bold">
                ${purchaseAmount === 50 ? '5.00' : purchaseAmount === 100 ? '10.00' : '20.00'}
              </span>
            </div>

          </div>

          <div className="mt-4">
            <PayPalCreditButton
              researcherId={researcherId}
              creditsAmount={purchaseAmount}
              onSuccess={handlePurchaseSuccess}
              onCancel={handlePurchaseCancel}
            />
          </div>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setPurchaseDialogOpen(false)}>
              {t('cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('upgradePlan')}</DialogTitle>
            <DialogDescription>
              {t('getMoreCredits')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-3 gap-4 py-4">
            {[
              { name: 'Basic', credits: 50, price: t('free') },
              { name: 'Premium', credits: 500, price: '$9.99/' + t('month') },
              { name: 'Advanced', credits: 2500, price: '$29.99/' + t('month') }
            ].map((plan) => (
              <div
                key={plan.name}
                className={`p-6 border-2 rounded-lg ${
                  credits.subscription_plan === plan.name.toLowerCase()
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200'
                }`}
              >
                <h3 className="text-lg font-bold mb-2">{t(plan.name.toLowerCase())}</h3>
                <p className="text-3xl font-bold text-purple-600 mb-2">{plan.credits}</p>
                <p className="text-sm text-gray-600 mb-4">{t('creditsPerMonth')}</p>
                <p className="text-xl font-bold mb-4">{plan.price}</p>
                <Button
                  className="w-full"
                  disabled={credits.subscription_plan === plan.name.toLowerCase()}
                >
                  {credits.subscription_plan === plan.name.toLowerCase() ? t('currentPlan') : t('upgrade')}
                </Button>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeDialogOpen(false)}>
              {t('close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AICreditsIndicator;
